/**
 * Default color values for the booking flow theme editor.
 * This is the shape every entry in `BOOKING_FLOW_PRESETS` must fully satisfy —
 * see `assertPresetIsComplete` in the component for the runtime check.
 */
export const DEFAULT_BOOKING_FLOW_THEME: any = {
  // General
  background: '#f5f7fb',
  cards: '#ffffff',
  borders: '#d9dfe8',
  divider: '#e7ebf1',

  // Header
  headerBackground: '#ffffff',
  headerBorder: '#e7ebf1',
  headerTitle: '#1d2b4f',

  // Room Card
  roomCardBackground: '#ffffff',
  roomCardBorder: '#d9dfe8',
  roomCardSelectedBorder: '#f39a27',
  roomCardTitle: '#17264a',
  roomCardDescription: '#2d3a59',
  roomCardMeta: '#7d8597',
  roomCardPrice: '#1e2b4c',
  roomCardButton: '#2f3fa3',
  roomCardButtonText: '#ffffff',
  roomCardButtonSelected: '#f39a27',
  roomCardButtonSelectedText: '#ffffff',

  // Room List & Media
  roomListBackground: '#ffffff',
  roomMediaBackground: '#f5f7fb',
  roomImageBorder: '#e7ebf1',

  // Empty State
  emptyStateBackground: '#f5f7fb',
  emptyStateText: '#7d8597',

  // Add-ons
  addonCardBackground: '#ffffff',
  addonCardBorder: '#d9dfe8',
  addonCardSelectedBorder: '#f39a27',
  addOnRoomLabel: '#7d8597',
  addOnRoomName: '#1d2b4f',
  addonTitle: '#1d2b4f',
  addonPrice: '#1d2b4f',
  addonCheckColor: '#f39a27',
  addonImageBackground: '#f5f7fb',
  addonToggleBackground: '#f5f7fb',
  addonToggleText: '#1d2b4f',

  // Payment
  paymentTitle: '#1d2b4f',
  paymentText: '#2d3a59',
  paymentDivider: '#e7ebf1',
  paymentMethodLabel: '#1d2b4f',
  paymentMethodSubtitle: '#7d8597',
  paymentNoteText: '#7d8597',
  paymentNoteLink: '#3046a5',
  paymentRequired: '#e44931',

  // Input Fields
  inputBackground: '#ffffff',
  inputText: '#1d2b4f',
  inputBorder: '#d9dfe8',
  inputFocusBorder: '#3046a5',
  inputPlaceholder: '#98a2b3',
  inputLabel: '#1d2b4f',
  phoneSelectBackground: '#ffffff',
  phoneSelectText: '#1d2b4f',
  phoneSelectBorder: '#d9dfe8',

  // Buttons
  primaryButtons: '#2f3fa3',
  primaryButtonText: '#ffffff',
  secondaryButtons: '#ffffff',
  secondaryButtonBorder: '#d9dfe8',
  secondaryButtonText: '#1d2b4f',
  buttonHover: '#1e2f87',
  backButtonBackground: 'transparent',
  backButtonIcon: '#667085',

  // Summary
  summaryBackground: '#f4f6fa',
  summaryBorder: '#d9dfe8',
  summaryTitle: '#1d2b4f',
  summaryText: '#2d3a59',
  summaryAmount: '#1d2b4f',
  summaryTotal: '#f39a27',
  summaryLabel: '#1d2b4f',
  summaryNote: '#7d8597',
  summaryButton: '#f5a12b',
  summaryButtonText: '#ffffff',
  summaryLink: '#3046a5',

  // Voucher
  voucherInputBackground: '#ffffff',
  voucherInputText: '#1d2b4f',
  voucherInputBorder: '#d9dfe8',
  voucherButtonBackground: '#2f3fa3',
  voucherButtonText: '#ffffff',
  voucherAppliedBackground: '#f0f7ef',
  voucherAppliedText: '#2e7d32',
  voucherRemoveText: '#c0392b',
  voucherErrorText: '#c0392b',
  discountAmountText: '#2e7d32',

  // Links & Icons
  linkColor: '#3046a5',
  linkHover: '#1d4ed8',
  iconColor: '#667085',

  // Slider
  sliderNavBackground: 'rgba(60,60,60,0.45)',
  sliderNavIcon: '#ffffff',
};

/** Vietnamese label shown in the UI for each theme key */
export const LIST_COLOR_OPTIONS: any[] = [
  // General
  { key: 'background', label: 'nen_chung' },
  { key: 'cards', label: 'nen_the' },
  { key: 'borders', label: 'vien_chung' },
  { key: 'divider', label: 'duong_phan_cach' },

  // Header
  { key: 'headerBackground', label: 'nen_header' },
  { key: 'headerBorder', label: 'vien_header' },
  { key: 'headerTitle', label: 'tieu_de_header' },

  // Room Card
  { key: 'roomCardBackground', label: 'nen_phong' },
  { key: 'roomCardBorder', label: 'vien_phong' },
  { key: 'roomCardSelectedBorder', label: 'vien_phong_duoc_chon' },
  { key: 'roomCardTitle', label: 'ten_phong' },
  { key: 'roomCardDescription', label: 'mo_ta_phong' },
  { key: 'roomCardMeta', label: 'thong_tin_phu_phong' },
  { key: 'roomCardPrice', label: 'gia_phong' },
  { key: 'roomCardButton', label: 'nut_chon_phong' },
  { key: 'roomCardButtonText', label: 'chu_nut_chon_phong' },
  { key: 'roomCardButtonSelected', label: 'nut_phong_duoc_chon' },
  { key: 'roomCardButtonSelectedText', label: 'chu_nut_phong_duoc_chon' },

  // Room List & Media
  { key: 'roomListBackground', label: 'nen_danh_sach_phong' },
  { key: 'roomMediaBackground', label: 'nen_media_phong' },
  { key: 'roomImageBorder', label: 'vien_anh_phong' },

  // Empty State
  { key: 'emptyStateBackground', label: 'nen_trang_thai_trong' },
  { key: 'emptyStateText', label: 'chu_trang_thai_trong' },

  // Add-ons
  { key: 'addonCardBackground', label: 'nen_tien_ich_mua_them' },
  { key: 'addonCardBorder', label: 'vien_tien_ich_mua_them' },
  { key: 'addonCardSelectedBorder', label: 'vien_tien_ich_mua_them_duoc_chon' },
  { key: 'addOnRoomLabel', label: 'label_phong_tien_ich_mua_them' },
  { key: 'addOnRoomName', label: 'ten_phong_tien_ich_mua_them' },
  { key: 'addonTitle', label: 'ten_tien_ich_mua_them' },
  { key: 'addonPrice', label: 'gia_tien_ich_mua_them' },
  { key: 'addonCheckColor', label: 'mau_check_tien_ich_mua_them' },
  { key: 'addonImageBackground', label: 'nen_anh_tien_ich' },
  { key: 'addonToggleBackground', label: 'nen_nut_expand' },
  { key: 'addonToggleText', label: 'chu_nut_expand' },

  // Payment
  { key: 'paymentTitle', label: 'tieu_de_thanh_toan' },
  { key: 'paymentText', label: 'text_thanh_toan' },
  { key: 'paymentDivider', label: 'nen_phan_cach_thanh_toan' },
  { key: 'paymentMethodLabel', label: 'label_phuong_thuc' },
  { key: 'paymentMethodSubtitle', label: 'mo_ta_phuong_thuc' },
  { key: 'paymentNoteText', label: 'chu_ghi_chu_thanh_toan' },
  { key: 'paymentNoteLink', label: 'link_ghi_chu_thanh_toan' },
  { key: 'paymentRequired', label: 'mau_sao_bat_buoc' },

  // Input Fields
  { key: 'inputBackground', label: 'nen_truong_nhap_lieu' },
  { key: 'inputText', label: 'chu_truong_nhap_lieu' },
  { key: 'inputBorder', label: 'vien_truong_nhap_lieu' },
  { key: 'inputFocusBorder', label: 'vien_truong_nhap_lieu_focus' },
  { key: 'inputPlaceholder', label: 'chu_placeholder' },
  { key: 'inputLabel', label: 'label_truong_nhap_lieu' },
  { key: 'phoneSelectBackground', label: 'nen_chon_dien_thoai' },
  { key: 'phoneSelectText', label: 'chu_chon_dien_thoai' },
  { key: 'phoneSelectBorder', label: 'vien_chon_dien_thoai' },

  // Buttons
  { key: 'primaryButtons', label: 'nen_nut_chinh' },
  { key: 'primaryButtonText', label: 'chu_nut_chinh' },
  { key: 'secondaryButtons', label: 'nen_nut_phu' },
  { key: 'secondaryButtonBorder', label: 'vien_nut_phu' },
  { key: 'secondaryButtonText', label: 'chu_nut_phu' },
  { key: 'buttonHover', label: 'nen_nut_hover' },
  { key: 'backButtonBackground', label: 'nen_nut_quay_lai' },
  { key: 'backButtonIcon', label: 'icon_quay_lai' },

  // Summary
  { key: 'summaryBackground', label: 'nen_tom_tat' },
  { key: 'summaryBorder', label: 'vien_tom_tat' },
  { key: 'summaryTitle', label: 'tieu_de_tom_tat' },
  { key: 'summaryText', label: 'text_tom_tat' },
  { key: 'summaryAmount', label: 'gia_tom_tat' },
  { key: 'summaryLabel', label: 'label_tong_tien' },
  { key: 'summaryTotal', label: 'tong_tien_tom_tat' },
  { key: 'summaryNote', label: 'chu_ghi_chu_tom_tat' },
  { key: 'summaryButton', label: 'nut_tom_tat' },
  { key: 'summaryButtonText', label: 'chu_nut_tom_tat' },
  { key: 'summaryLink', label: 'link_tom_tat' },

  // Voucher
  { key: 'voucherInputBackground', label: 'nen_o_nhap_ma_giam_gia' },
  { key: 'voucherInputText', label: 'chu_o_nhap_ma_giam_gia' },
  { key: 'voucherInputBorder', label: 'vien_o_nhap_ma_giam_gia' },
  { key: 'voucherButtonBackground', label: 'nen_nut_ap_dung_ma' },
  { key: 'voucherButtonText', label: 'chu_nut_ap_dung_ma' },
  { key: 'voucherAppliedBackground', label: 'nen_ma_da_ap_dung' },
  { key: 'voucherAppliedText', label: 'chu_ma_da_ap_dung' },
  { key: 'voucherRemoveText', label: 'chu_nut_xoa_ma' },
  { key: 'voucherErrorText', label: 'chu_loi_ma_giam_gia' },
  { key: 'discountAmountText', label: 'chu_so_tien_giam' },

  // Links & Icons
  { key: 'linkColor', label: 'mau_link' },
  { key: 'linkHover', label: 'mau_link_hover' },
  { key: 'iconColor', label: 'mau_icon' },

  // Slider
  { key: 'sliderNavBackground', label: 'nen_nut_slider' },
  { key: 'sliderNavIcon', label: 'icon_slider' },
];

/**
 * Maps each theme key to the CSS property + list of style-class keys
 * (in `this.class`) that `updateGroupColor()` should write the value into.
 */
export const BOOKING_FLOW_COLOR_GROUPS: any = {
  // General
  background: { property: 'background-color', classes: ['booking-flow'] },
  cards: { property: 'background-color', classes: ['choose-room__panel', 'add-ons__panel', 'payment__panel', 'summary__card'] },
  borders: { property: 'border-color', classes: ['choose-room__panel', 'add-ons__panel', 'payment__panel', 'summary__card'] },
  divider: { property: 'border-color', classes: ['summary__section', 'add-ons__room-row'] },

  // Header
  headerBackground: { property: 'background-color', classes: ['choose-room__header', 'add-ons__header', 'payment__header', 'summary__header'] },
  headerBorder: { property: 'border-bottom-color', classes: ['choose-room__header', 'add-ons__header', 'payment__header', 'summary__header'] },
  headerTitle: { property: 'color', classes: ['choose-room__title', 'add-ons__title', 'payment__title', 'summary__header-title'] },

  // Room Card
  roomCardBackground: { property: 'background-color', classes: ['choose-room__card', 'choose-room__card-selected'] },
  roomCardBorder: { property: 'border-color', classes: ['choose-room__card'] },
  roomCardSelectedBorder: { property: 'border-color', classes: ['choose-room__card-selected'] },
  roomCardTitle: { property: 'color', classes: ['choose-room__name'] },
  roomCardDescription: { property: 'color', classes: ['choose-room__desc'] },
  roomCardMeta: { property: 'color', classes: ['choose-room__meta'] },
  roomCardPrice: { property: 'color', classes: ['choose-room__price-line'] },
  roomCardButton: { property: 'background-color', classes: ['choose-room__select-btn'] },
  roomCardButtonText: { property: 'color', classes: ['choose-room__select-btn'] },
  roomCardButtonSelected: { property: 'background-color', classes: ['choose-room__select-btn-selected'] },
  roomCardButtonSelectedText: { property: 'color', classes: ['choose-room__select-btn-selected'] },

  // Room List & Media
  roomListBackground: { property: 'background-color', classes: ['choose-room__list'] },
  roomMediaBackground: { property: 'background-color', classes: ['choose-room__media'] },
  roomImageBorder: { property: 'border-color', classes: ['choose-room__image'] },

  // Empty State
  emptyStateBackground: { property: 'background-color', classes: ['choose-room__empty'] },
  emptyStateText: { property: 'color', classes: ['choose-room__empty'] },

  // Add-ons
  addonCardBackground: { property: 'background-color', classes: ['add-ons__item', 'add-ons__item-selected'] },
  addonCardBorder: { property: 'border-color', classes: ['add-ons__item'] },
  addonCardSelectedBorder: { property: 'border-color', classes: ['add-ons__item-selected'] },
  addOnRoomLabel: { property: 'color', classes: ['add-ons__room-label'] },
  addOnRoomName: { property: 'color', classes: ['add-ons__room-name'] },
  addonTitle: { property: 'color', classes: ['add-ons__item-title'] },
  addonPrice: { property: 'color', classes: ['add-ons__item-price'] },
  addonCheckColor: { property: 'color', classes: ['add-ons__check'] },
  addonImageBackground: { property: 'background-color', classes: ['add-ons__image'] },
  addonToggleBackground: { property: 'background-color', classes: ['add-ons__toggle'] },
  addonToggleText: { property: 'color', classes: ['add-ons__toggle'] },

  // Payment
  paymentTitle: { property: 'color', classes: ['payment__title', 'payment__block-title'] },
  paymentText: { property: 'color', classes: ['payment__hint', 'payment__note'] },
  paymentDivider: { property: 'background-color', classes: ['payment__divider'] },
  paymentMethodLabel: { property: 'color', classes: ['payment__method-label'] },
  paymentMethodSubtitle: { property: 'color', classes: ['payment__method-subtitle'] },
  paymentNoteText: { property: 'color', classes: ['payment__note'] },
  paymentNoteLink: { property: 'color', classes: ['payment__note-link'] },
  paymentRequired: { property: 'color', classes: ['payment__required'] },

  // Input Fields
  inputBackground: { property: 'background-color', classes: ['payment__input', 'payment__select', 'payment__textarea'] },
  inputText: { property: 'color', classes: ['payment__input', 'payment__select', 'payment__textarea'] },
  inputBorder: { property: 'border-color', classes: ['payment__input', 'payment__select', 'payment__textarea', 'payment__phone-select', 'payment__phone-input'] },
  inputFocusBorder: { property: 'border-color', classes: ['payment__input:focus', 'payment__select:focus', 'payment__textarea:focus'] },
  inputPlaceholder: { property: 'color', classes: ['payment__input::placeholder', 'payment__select::placeholder', 'payment__textarea::placeholder'] },
  inputLabel: { property: 'color', classes: ['payment__field-label', 'payment__field-group-label'] },
  phoneSelectBackground: { property: 'background-color', classes: ['payment__phone-select'] },
  phoneSelectText: { property: 'color', classes: ['payment__phone-select'] },
  phoneSelectBorder: { property: 'border-color', classes: ['payment__phone-select'] },

  // Buttons
  primaryButtons: { property: 'background-color', classes: ['choose-room__select-btn', 'summary__action-btn', 'payment__action-btn'] },
  primaryButtonText: { property: 'color', classes: ['choose-room__select-btn', 'summary__action-btn', 'payment__action-btn'] },
  secondaryButtons: { property: 'background-color', classes: ['choose-room__link', 'summary__link'] },
  secondaryButtonBorder: { property: 'border-color', classes: ['choose-room__link', 'summary__link'] },
  secondaryButtonText: { property: 'color', classes: ['choose-room__link', 'summary__link'] },
  buttonHover: { property: 'background-color', classes: ['choose-room__select-btn:hover', 'summary__action-btn:hover', 'payment__action-btn:hover'] },
  backButtonBackground: { property: 'background-color', classes: ['add-ons__back-btn', 'payment__back-btn'] },
  backButtonIcon: { property: 'color', classes: ['add-ons__back-icon', 'payment__back-icon'] },

  // Summary
  summaryBackground: { property: 'background-color', classes: ['summary__addons'] },
  summaryBorder: { property: 'border-color', classes: ['summary__card'] },
  summaryTitle: { property: 'color', classes: ['summary__section-title'] },
  summaryText: { property: 'color', classes: ['summary__section-text', 'summary__section-subtext'] },
  summaryLabel: { property: 'color', classes: ['summary__total-label'] },
  summaryAmount: { property: 'color', classes: ['summary__amount'] },
  summaryTotal: { property: 'color', classes: ['summary__total-amount'] },
  summaryNote: { property: 'color', classes: ['summary__total-note'] },
  summaryButton: { property: 'background-color', classes: ['summary__action-btn', 'payment__action-btn'] },
  summaryButtonText: { property: 'color', classes: ['summary__action-btn', 'payment__action-btn'] },
  summaryLink: { property: 'color', classes: ['summary__link'] },

  // Voucher
  voucherInputBackground: { property: 'background-color', classes: ['summary__voucher-input'] },
  voucherInputText: { property: 'color', classes: ['summary__voucher-input'] },
  voucherInputBorder: { property: 'border-color', classes: ['summary__voucher-input'] },
  voucherButtonBackground: { property: 'background-color', classes: ['summary__voucher-btn'] },
  voucherButtonText: { property: 'color', classes: ['summary__voucher-btn'] },
  voucherAppliedBackground: { property: 'background-color', classes: ['summary__voucher-applied'] },
  voucherAppliedText: { property: 'color', classes: ['summary__voucher-applied'] },
  voucherRemoveText: { property: 'color', classes: ['summary__voucher-remove'] },
  voucherErrorText: { property: 'color', classes: ['summary__voucher-message'] },
  discountAmountText: { property: 'color', classes: ['summary__discount-amount'] },

  // Links & Icons
  linkColor: { property: 'color', classes: ['choose-room__link', 'summary__link', 'payment__note-link'] },
  linkHover: { property: 'color', classes: ['choose-room__link:hover', 'summary__link:hover', 'payment__note-link:hover'] },
  iconColor: { property: 'color', classes: ['choose-room__meta-icon', 'payment__back-icon', 'add-ons__back-icon'] },

  // Slider
  sliderNavBackground: { property: 'background-color', classes: ['choose-room__nav'] },
  sliderNavIcon: { property: 'color', classes: ['choose-room__nav'] },
};

/** Groups color keys under a collapsible UI section, in display order */
export const COLOR_SECTIONS: any[] = [
  {
    title: 'chung',
    fields: ['background', 'cards', 'borders', 'divider'],
  },
  {
    title: 'Header',
    fields: ['headerBackground', 'headerBorder', 'headerTitle'],
  },
  {
    title: 'phong',
    fields: [
      'roomCardBackground', 'roomCardBorder', 'roomCardSelectedBorder',
      'roomCardTitle', 'roomCardDescription', 'roomCardMeta',
      'roomCardPrice',
      'roomCardButton', 'roomCardButtonText',
      'roomCardButtonSelected', 'roomCardButtonSelectedText',
      'roomListBackground', 'roomMediaBackground', 'roomImageBorder',
    ],
  },
  {
    title: 'trang_thai_trong',
    fields: ['emptyStateBackground', 'emptyStateText'],
  },
  {
    title: 'tien_ich_mua_them',
    fields: [
      'addonCardBackground', 'addonCardBorder', 'addonCardSelectedBorder',
      'addOnRoomLabel', 'addOnRoomName', 'addonTitle', 'addonPrice', 'addonCheckColor',
      'addonImageBackground', 'addonToggleBackground', 'addonToggleText',
    ],
  },
  {
    title: 'thanh_toan',
    fields: [
      'paymentTitle', 'paymentText', 'paymentDivider',
      'paymentMethodLabel', 'paymentMethodSubtitle', 'paymentNoteText', 'paymentNoteLink', 'paymentRequired',
      'inputBackground', 'inputText', 'inputBorder', 'inputFocusBorder', 'inputPlaceholder', 'inputLabel',
      'phoneSelectBackground', 'phoneSelectText', 'phoneSelectBorder',
    ],
  },
  {
    title: 'nut_bam',
    fields: [
      'primaryButtons', 'primaryButtonText',
      'secondaryButtons', 'secondaryButtonBorder', 'secondaryButtonText',
      'buttonHover', 'backButtonBackground', 'backButtonIcon',
    ],
  },
  {
    title: 'tom_tat',
    fields: [
      'summaryBackground', 'summaryBorder', 'summaryTitle', 'summaryText',
      'summaryAmount', 'summaryLabel', 'summaryTotal', 'summaryNote',
      'summaryButton', 'summaryButtonText', 'summaryLink',
    ],
  },
  {
    title: 'ma_giam_gia',
    fields: [
      'voucherInputBackground', 'voucherInputText', 'voucherInputBorder',
      'voucherButtonBackground', 'voucherButtonText',
      'voucherAppliedBackground', 'voucherAppliedText',
      'voucherRemoveText', 'voucherErrorText', 'discountAmountText',
    ],
  },
  {
    title: 'khac',
    fields: ['linkColor', 'linkHover', 'iconColor', 'sliderNavBackground', 'sliderNavIcon'],
  },
];